"use client";

import { useParams, useRouter } from "next/navigation";
import { useEffect } from "react";

import { useAppContext } from "../../context";
import {
  getSession,
  signOut,
  fetchSubscriptions,
  fetchCustomerData,
} from "../../actions";

export function AuthHydrator({ children }: { children: React.ReactNode }) {
  const { setUserInfo, setAuth, setOnLogout, setSubscriptionsInfo } =
    useAppContext();
  const router = useRouter();
  const params = useParams<{ locale: string }>();

  useEffect(() => {
    async function hydrate() {
      const [session, subscriptions, customerData] = await Promise.all([
        getSession(),
        fetchSubscriptions().catch(() => null),
        fetchCustomerData().catch(() => null),
      ]);

      if (session.isAuthenticated && session.user) {
        // GD-154: the session is read from the token, which keeps the name it
        // was issued with. The profile API holds the one saved since.
        const saved: { firstName?: string; lastName?: string } | null =
          customerData?.customerInfo ?? customerData;
        setUserInfo({
          ...session.user,
          firstName: saved?.firstName || session.user.firstName,
          lastName: saved?.lastName || session.user.lastName,
        });
        setAuth({ isAuthenticated: true, isLoading: false });
        if (subscriptions) setSubscriptionsInfo(subscriptions);
      } else {
        setAuth({ isAuthenticated: false, isLoading: false });
        router.replace(`/${params.locale}/login`);
      }
    }
    hydrate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setOnLogout(() => {
      signOut().then(() => {
        setAuth({ isAuthenticated: false, isLoading: false });
        setUserInfo({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          phoneCountryCode: "",
          documentId: "",
          passportId: "",
          avatarId: "",
        });
        router.replace(`/${params.locale}/login`);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.locale]);

  return <>{children}</>;
}
